import { useState, useEffect } from 'react';
import { useIntl } from '@leagueplatform/locales';
import type { AppTenantConfig } from '@web-config-app/core';
import { useConfigApp } from '@web-config-app/core-react';
import type { TenantId } from '../../types';
import { captureMessageWithOwner } from '../../utils/sentry-helpers';
import { getTenantConfig } from '../../utils/get-tenant-config';
import { useTenantScopedTokenClaims } from '../use-tenant-scoped-token-claims/use-tenant-scoped-token-claims';

type TenantConfigResult = {
  configMap: Record<TenantId, AppTenantConfig>;
  unrecognisedIds: string[];
};

/**
 * Resolves a list of raw tenant ID strings into a config map and a list of
 * IDs that couldn't be matched to a known tenant.
 *
 */
const resolveTenantIds = (tenantIds: string[]): TenantConfigResult =>
  tenantIds.reduce<TenantConfigResult>(
    (acc, id) => {
      const config = getTenantConfig(id);
      if (config) {
        acc.configMap[config.configApp.tenantId as TenantId] = config;
      } else {
        acc.unrecognisedIds.push(id);
      }
      return acc;
    },
    { configMap: {} as TenantConfigResult['configMap'], unrecognisedIds: [] },
  );

/**
 * Builds a map of the tenants the current user has access to.
 *
 * Consumes `useTenantScopedTokenClaims` to fetch a token scoped to the
 * current tenant's organization/audience and decode its `accessible_tenants`
 * claim. Each tenant ID is then resolved to a full `AppTenantConfig` via
 * `getTenantConfig`. Unrecognised IDs are filtered out and reported to Sentry.
 *
 * Only runs when `enabled` is `true`.
 *
 * @returns tenantConfigMap - `Record<TenantId, AppTenantConfig>` or `undefined`
 * @returns isLoading - `true` while the token is being fetched and decoded
 * @returns error - Set when the token fetch fails or no accessible tenants are found
 */
export const useAccessibleTenants = (enabled: boolean) => {
  const { tenantId: currentTenantId } = useConfigApp();
  const { formatMessage } = useIntl();
  const {
    accessibleTenantIds,
    isLoading: isClaimsLoading,
    error: claimsError,
  } = useTenantScopedTokenClaims(currentTenantId, enabled);

  const [tenantConfigMap, setTenantConfigMap] = useState<
    Record<TenantId, AppTenantConfig> | undefined
  >(undefined);
  const [resolveError, setResolveError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) {
      setTenantConfigMap(undefined);
      setResolveError(null);
      return;
    }

    // Wait until claims have loaded
    if (isClaimsLoading || claimsError) {
      return;
    }

    const { configMap, unrecognisedIds } =
      resolveTenantIds(accessibleTenantIds);

    if (unrecognisedIds.length > 0) {
      captureMessageWithOwner(
        `useAccessibleTenants: unrecognised tenant IDs in accessible_tenants claim: ${unrecognisedIds.join(
          ', ',
        )}`,
        {
          severityLevel: 'warning',
          context: { tenantIds: { unrecognised: unrecognisedIds } },
        },
      );
    }

    if (Object.keys(configMap).length === 0) {
      setResolveError(
        new Error(formatMessage({ id: 'NO_ACCESSIBLE_TENANTS_FOUND' })),
      );
      setTenantConfigMap(undefined);
    } else {
      setTenantConfigMap(configMap);
      setResolveError(null);
    }
  }, [
    enabled,
    isClaimsLoading,
    claimsError,
    accessibleTenantIds,
    formatMessage,
  ]);

  return {
    tenantConfigMap,
    isLoading: isClaimsLoading,
    error: claimsError ?? resolveError,
  };
};
